import { useCallback, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Check, Flag, MessageSquareOff, X } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import {
  fetchReports,
  resolveReport,
  dismissReport,
  deleteReportedMessage,
  type AdminReport,
} from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'
import { InfoRow, MonoValue, RelativeTime } from '@/components/DataDisplay'
import { Tag } from '@/components/StatusBadge'
import { Dialog } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Field, Textarea } from '@/components/ui/input'
import { useToast } from '@/providers/UiProviders'

/**
 * A6 — черга скарг. GET /admin/reports: реальні дані з ReportAggregate, наповнюється
 * шістьма report-RPC (account.reportPeer, messages.report, account.reportProfilePhoto,
 * channels.reportSpam, messages.reportSpam, stories.report) — раніше жоден з них нічого
 * не зберігав, тільки писав у лог і повертав true.
 */
export function ReportsScreen() {
  const t = useT()
  const { toast } = useToast()
  const [reloadKey, setReloadKey] = useState(0)
  const [action, setAction] = useState<{ report: AdminReport; kind: 'resolve' | 'dismiss' } | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<AdminReport | null>(null)

  const columns = useMemo<AdminColumn<AdminReport>[]>(
    () => [
      {
        key: 'created_at',
        header: t.reports.createdAt,
        type: 'date',
        width: 140,
      },
      {
        key: 'reporter_user_id',
        header: t.reports.reporter,
        type: 'text',
        width: 160,
        render: (r) => (
          <Link to={`/users/${r.reporter_user_id}`} className="text-accent hover:underline">
            {r.reporter_username ? `@${r.reporter_username}` : `#${r.reporter_user_id}`}
          </Link>
        ),
      },
      {
        key: 'target_type',
        header: t.reports.target,
        type: 'text',
        minWidth: 200,
        render: (r) => (
          <span className="text-sm">
            <Tag>{r.target_type}</Tag>{' '}
            <Link to={`/users/${r.target_peer_id}`} className="text-accent hover:underline">
              {r.target_username ? `@${r.target_username}` : `#${r.target_peer_id}`}
            </Link>
            {r.message_id != null && <span className="text-tertiary"> · msg {r.message_id}</span>}
          </span>
        ),
      },
      { key: 'reason', header: t.reports.reason, type: 'text', width: 140 },
      {
        key: 'status',
        header: t.reports.status,
        type: 'enum',
        width: 120,
        options: [
          { value: 'open', label: t.reports.statusOpen, status: 'review' },
          { value: 'resolved', label: t.reports.statusResolved, status: 'active' },
          { value: 'dismissed', label: t.reports.statusDismissed, status: 'deleted' },
        ],
      },
    ],
    [t],
  )

  const rowActions = useMemo<RowAction<AdminReport>[]>(
    () => [
      {
        id: 'resolve',
        label: t.reports.resolve,
        icon: <Check size={13} />,
        permission: PERMISSIONS.USERS_MODERATE,
        visible: (r) => r.status === 'open',
        onSelect: (r) => setAction({ report: r, kind: 'resolve' }),
      },
      {
        id: 'dismiss',
        label: t.reports.dismiss,
        icon: <X size={13} />,
        danger: true,
        permission: PERMISSIONS.USERS_MODERATE,
        visible: (r) => r.status === 'open',
        onSelect: (r) => setAction({ report: r, kind: 'dismiss' }),
      },
      {
        id: 'delete-message',
        label: t.reports.deleteMessage,
        icon: <MessageSquareOff size={13} />,
        danger: true,
        permission: PERMISSIONS.USERS_MODERATE,
        visible: (r) => r.message_id != null,
        onSelect: (r) => setDeleteTarget(r),
      },
    ],
    [t],
  )

  const fetcher = useCallback(fetchReports, [])

  return (
    <>
      <PageHeader title={t.reports.title} screenId="A6.01" verified />
      <DataTable<AdminReport>
        key={reloadKey}
        screenId="A6.01"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => r.id}
        rowActions={rowActions}
        emptyState={{ icon: <Flag size={28} strokeWidth={1.5} />, title: t.reports.emptyTitle }}
        detailPanel={(r) => (
          <div>
            <InfoRow label={t.col.id}>
              <MonoValue value={r.id} />
            </InfoRow>
            <InfoRow label={t.reports.comment}>{r.comment || '—'}</InfoRow>
            <InfoRow label={t.reports.createdAt}>
              <RelativeTime value={r.created_at} />
            </InfoRow>
            {r.status !== 'open' && (
              <>
                <InfoRow label={t.reports.resolvedBy}>{r.resolved_by_admin_id ?? '—'}</InfoRow>
                <InfoRow label={t.reports.resolutionLabel}>{r.resolution || '—'}</InfoRow>
              </>
            )}
          </div>
        )}
      />

      {action && (
        <ResolveDialog
          report={action.report}
          kind={action.kind}
          onClose={() => setAction(null)}
          onDone={() => {
            setAction(null)
            setReloadKey((k) => k + 1)
          }}
        />
      )}

      {deleteTarget && (
        <DeleteMessageDialog
          report={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onDone={() => {
            setDeleteTarget(null)
            setReloadKey((k) => k + 1)
          }}
        />
      )}
    </>
  )
}

function DeleteMessageDialog({
  report,
  onClose,
  onDone,
}: {
  report: AdminReport
  onClose: () => void
  onDone: () => void
}) {
  const t = useT()
  const { toast } = useToast()
  const [notice, setNotice] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const submit = async () => {
    setSubmitting(true)
    try {
      await deleteReportedMessage(report.id, notice.trim() || undefined)
      toast(t.users.actionDone, { tone: 'success' })
      onDone()
    } catch (e) {
      toast(t.users.actionFailed, {
        tone: 'error',
        ...(e instanceof Error && e.message ? { description: e.message } : {}),
      })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title={t.reports.deleteMessageTitle}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t.common.cancel}
          </Button>
          <Button variant="danger" onClick={() => void submit()} disabled={submitting}>
            {t.reports.deleteMessage}
          </Button>
        </>
      }
    >
      <Field label={t.reports.deleteMessageNoticeLabel}>
        <Textarea
          value={notice}
          onChange={(e) => setNotice(e.target.value)}
          rows={3}
          placeholder={t.reports.deleteMessageDefaultHint}
        />
      </Field>
    </Dialog>
  )
}

function ResolveDialog({
  report,
  kind,
  onClose,
  onDone,
}: {
  report: AdminReport
  kind: 'resolve' | 'dismiss'
  onClose: () => void
  onDone: () => void
}) {
  const t = useT()
  const { toast } = useToast()
  const [resolution, setResolution] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const submit = async () => {
    setSubmitting(true)
    try {
      const fn = kind === 'resolve' ? resolveReport : dismissReport
      await fn(report.id, resolution.trim() || undefined)
      toast(t.users.actionDone, { tone: 'success' })
      onDone()
    } catch (e) {
      toast(t.users.actionFailed, {
        tone: 'error',
        ...(e instanceof Error && e.message ? { description: e.message } : {}),
      })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title={kind === 'resolve' ? t.reports.resolveTitle : t.reports.dismissTitle}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t.common.cancel}
          </Button>
          <Button
            variant={kind === 'dismiss' ? 'danger' : 'primary'}
            onClick={() => void submit()}
            disabled={submitting}
          >
            {kind === 'resolve' ? t.reports.resolve : t.reports.dismiss}
          </Button>
        </>
      }
    >
      <Field label={t.reports.resolutionLabel}>
        <Textarea value={resolution} onChange={(e) => setResolution(e.target.value)} rows={3} />
      </Field>
    </Dialog>
  )
}
